import { decryptString, encryptString } from "@/lib";
import { envStorage } from "@/lib/storage/envIsolatedStorage";
import { resolveStoredValue } from "./legacyEncryptedValue";
import { StorageKeys } from "./storage-keys";

function getEnvKey(key: string): string {
  const env = import.meta.env.VITE_ENV || "development";
  return `${env}-${key}`;
}

export function setItem(key: string, value: string) {
  const encryptedValue = encryptString(value);
  envStorage.setItem(key, encryptedValue);
}

export function getItem(key: string) {
  const encryptedValue = envStorage.getItem(key);
  if (encryptedValue) {
    const decryptedValue = decryptString(encryptedValue);

    // If decryption failed (returns empty string), clear the corrupted data
    if (decryptedValue === "" && encryptedValue !== "") {
      console.warn(`Clearing corrupted encrypted data for key: ${key}`);
      envStorage.removeItem(key);
      return null;
    }

    return decryptedValue || null;
  }
  return null;
}

export function removeItem(key: string) {
  envStorage.removeItem(key);
}

export function clear() {
  envStorage.clear();
}

/**
 * Read a plain value from sessionStorage. Values written before ECHO-2318 were AES-encrypted
 * with a bundle-shipped key; those are decrypted once and rewritten in plain form, and legacy
 * values that no longer decrypt are removed.
 */
function readSessionValue(key: string): string | null {
  try {
    const envKey = getEnvKey(key);
    const raw = sessionStorage.getItem(envKey);
    const { value, migrated } = resolveStoredValue(raw, decryptString);

    if (raw && value === null) {
      console.warn(`Clearing undecryptable legacy value for key: ${key}`);
      sessionStorage.removeItem(envKey);
    } else if (migrated && value) {
      persistMigratedValue(envKey, value);
    }

    return value;
  } catch {
    return null;
  }
}

/** Best-effort write-back: a failed write (e.g. quota) must not discard the migrated value. */
function persistMigratedValue(envKey: string, value: string) {
  try {
    sessionStorage.setItem(envKey, value);
  } catch (error) {
    console.warn(`Failed to rewrite migrated value for key: ${envKey}`, error);
  }
}

function writeSessionValue(key: string, value: string) {
  try {
    sessionStorage.setItem(getEnvKey(key), value);
  } catch (error) {
    console.error(`Failed to set ${key}:`, error);
  }
}

export function getAccessToken() {
  return readSessionValue(StorageKeys.accessToken);
}

export function getRefreshToken() {
  return readSessionValue(StorageKeys.refreshToken);
}

export function setAccessToken(token: string) {
  writeSessionValue(StorageKeys.accessToken, token);
}

export function setRefreshToken(token: string) {
  writeSessionValue(StorageKeys.refreshToken, token);
}

export function clearTokens() {
  sessionStorage.removeItem(getEnvKey(StorageKeys.accessToken));
  sessionStorage.removeItem(getEnvKey(StorageKeys.refreshToken));
}

export function getSessionId() {
  return readSessionValue(StorageKeys.sessionId);
}

export function setSessionId(sessionId: string) {
  writeSessionValue(StorageKeys.sessionId, sessionId);
}

export function clearSession() {
  sessionStorage.removeItem(getEnvKey(StorageKeys.sessionId));
}

const LocalStorage = {
  setItem,
  getItem,
  removeItem,
  clear,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  clearTokens,
  getSessionId,
  setSessionId,
  clearSession,
};

export default LocalStorage;
